import { FC, useMemo } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Checkbox,
	TextField,
	Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { updatePrpPostalOperatorSettingsRequest } from 'redux/reducers/prp/reducer';
import { getPrpPostalOperatorSettingsSaveLoading } from 'redux/reducers/prp/selectors';
import { getEnumsSupportedCurrency } from 'redux/reducers/emManagement/selectors';
import { TPostalOperatorSettingsDialogProps, TPostalOperatorSettingsFormValues } from './types';

const validationSchema = Yup.object({
	postalOperatorHandlingFee: Yup.number()
		.required('Handling fee is required')
		.min(0, 'Must be >= 0'),
});

export const PostalOperatorSettingsDialog: FC<TPostalOperatorSettingsDialogProps> = ({
	open,
	onClose,
	emId,
	item,
}) => {
	const dispatch = useDispatch();
	const loading = useSelector(getPrpPostalOperatorSettingsSaveLoading);
	const supportedCurrency = useSelector(getEnumsSupportedCurrency);

	const currencyOptions = useMemo(
		() =>
			(supportedCurrency ?? []).map((option) => ({
				id: Number(option.value),
				name: option.displayName,
			})),
		[supportedCurrency],
	);

	const formik = useFormik<TPostalOperatorSettingsFormValues>({
		initialValues: {
			collectIossDuty: item?.collectIossDuty ?? false,
			postalOperatorHandlingFee: item?.postalOperatorHandlingFee ?? ('' as unknown as number),
			handlingFeeCurrencyId: item?.handlingFeeCurrencyId ?? null,
			collectCustomsProcessingFee: item?.collectCustomsProcessingFee ?? false,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			if (!item) return;
			dispatch(
				updatePrpPostalOperatorSettingsRequest({
					emId,
					id: item.id,
					body: {
						collectIossDuty: values.collectIossDuty,
						postalOperatorHandlingFee: Number(values.postalOperatorHandlingFee),
						handlingFeeCurrencyId: values.handlingFeeCurrencyId,
						collectCustomsProcessingFee: values.collectCustomsProcessingFee,
					},
				}),
			);
			onClose();
		},
	});

	const selectedCurrency = useMemo(
		() =>
			currencyOptions.find((option) => option.id === formik.values.handlingFeeCurrencyId) ?? null,
		[currencyOptions, formik.values.handlingFeeCurrencyId],
	);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit Postal Operator Settings</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<Typography variant="body2" color="text.secondary">
						{item?.postalOperatorName} {item?.countryName ? `(${item.countryName})` : ''}
					</Typography>
					<TextField
						label="Handling Fee"
						name="postalOperatorHandlingFee"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.postalOperatorHandlingFee}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.postalOperatorHandlingFee && !!formik.errors.postalOperatorHandlingFee
						}
						helperText={
							formik.touched.postalOperatorHandlingFee && formik.errors.postalOperatorHandlingFee
						}
					/>
					<Autocomplete
						options={currencyOptions}
						value={selectedCurrency}
						getOptionLabel={(option) => option.name}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						onChange={(event, option) =>
							formik.setFieldValue('handlingFeeCurrencyId', option?.id ?? null)
						}
						renderInput={(params) => (
							<TextField {...params} label="Handling Fee Currency" size="small" />
						)}
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="collectIossDuty"
								checked={formik.values.collectIossDuty}
								onChange={formik.handleChange}
							/>
						}
						label="Collect IOSS Duty"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="collectCustomsProcessingFee"
								checked={formik.values.collectCustomsProcessingFee}
								onChange={formik.handleChange}
							/>
						}
						label="Collect Customs Processing Fee"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
